/**
 * Editor Store - Canvas, Selection, and Visualization State
 *
 * Centralizes all editor canvas state:
 * - Canvas state (zoom, pan, rotation)
 * - Selection state (selected elements, hover)
 * - Visualization state (element counts, relationships)
 * - Relationship editing state
 *
 * Store-First Pattern:
 * - Canvas state lives here, not in component
 * - JointJS updates trigger store updates
 * - Components read via selectors
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { ElementId, TypeId, RelationshipKey } from '../types/branded'

/**
 * Canvas State Interface
 */
export interface CanvasState {
  zoom: number
  pan: { x: number; y: number }
  rotation: number
  width: number
  height: number
}

/**
 * Selection State Interface
 */
export interface SelectionState {
  selectedTypeId: TypeId | null
  selectedElementId: ElementId | null
  selectedElementIds: ElementId[]
  hoveredElementId: ElementId | null
}

/**
 * Visualization State Interface
 */
export interface VisualizationState {
  elementCount: number
  objectsByLayer: Record<string, number>
  relationshipCount: number
}

/**
 * Relationship Edit State Interface
 */
export interface RelationshipEditState {
  isEditing: boolean
  relationKey: RelationshipKey | null
  propertyKey: string | null
  targetElementIds: ElementId[]
}

/**
 * Complete Editor State
 */
export interface EditorState {
  canvas: CanvasState
  selection: SelectionState
  visualization: VisualizationState
  relationshipEdit: RelationshipEditState
  defaultObjectSize: number
}

/**
 * Editor Actions Interface
 */
export interface EditorActions {
  // Canvas Actions
  setZoom: (zoom: number) => void
  setPan: (pan: { x: number; y: number }) => void
  setRotation: (rotation: number) => void
  setCanvasSize: (width: number, height: number) => void
  resetCanvas: () => void

  // Selection Actions
  setSelectedTypeId: (typeId: TypeId | null) => void
  setSelectedElementId: (elementId: ElementId | null) => void
  setSelectedElementIds: (elementIds: ElementId[]) => void
  addSelectedElementId: (elementId: ElementId) => void
  removeSelectedElementId: (elementId: ElementId) => void
  setHoveredElementId: (elementId: ElementId | null) => void
  clearSelection: () => void

  // Visualization Actions
  setElementCount: (count: number) => void
  setObjectsByLayer: (objects: Record<string, number>) => void
  setRelationshipCount: (count: number) => void
  updateVisualization: (
    elementCount: number,
    objectsByLayer: Record<string, number>,
    relationshipCount: number
  ) => void

  // Relationship Edit Actions
  startRelationshipEdit: (
    relationKey: RelationshipKey,
    propertyKey: string,
    targetElementIds: ElementId[]
  ) => void
  updateRelationshipTargets: (targetElementIds: ElementId[]) => void
  endRelationshipEdit: () => void

  // Object Size Actions
  setDefaultObjectSize: (size: number) => void

  // Reset Actions
  resetEditor: () => void
}

/**
 * Complete Editor Store
 */
export type EditorStore = EditorState & EditorActions

/**
 * Initial Canvas State
 */
const initialCanvasState: CanvasState = {
  zoom: 1,
  pan: { x: 0, y: 0 },
  rotation: 0,
  width: 0,
  height: 0
}

/**
 * Initial Selection State
 */
const initialSelectionState: SelectionState = {
  selectedTypeId: null,
  selectedElementId: null,
  selectedElementIds: [],
  hoveredElementId: null
}

/**
 * Initial Visualization State
 */
const initialVisualizationState: VisualizationState = {
  elementCount: 0,
  objectsByLayer: {},
  relationshipCount: 0
}

/**
 * Initial Relationship Edit State
 */
const initialRelationshipEditState: RelationshipEditState = {
  isEditing: false,
  relationKey: null,
  propertyKey: null,
  targetElementIds: []
}

/**
 * Editor Store Implementation
 */
export const useEditorStore = create<EditorStore>()(
  devtools(
    (set) => ({
      // Initial State
      canvas: initialCanvasState,
      selection: initialSelectionState,
      visualization: initialVisualizationState,
      relationshipEdit: initialRelationshipEditState,
      defaultObjectSize: 100,

      // Canvas Actions
      setZoom: (zoom) =>
        set(
          (state) => ({
            canvas: {
              ...state.canvas,
              zoom
            }
          }),
          false,
          'editor/setZoom'
        ),

      setPan: (pan) =>
        set(
          (state) => ({
            canvas: {
              ...state.canvas,
              pan
            }
          }),
          false,
          'editor/setPan'
        ),

      setRotation: (rotation) =>
        set(
          (state) => ({
            canvas: {
              ...state.canvas,
              rotation
            }
          }),
          false,
          'editor/setRotation'
        ),

      setCanvasSize: (width, height) =>
        set(
          (state) => ({
            canvas: {
              ...state.canvas,
              width,
              height
            }
          }),
          false,
          'editor/setCanvasSize'
        ),

      resetCanvas: () =>
        set(
          {
            canvas: initialCanvasState
          },
          false,
          'editor/resetCanvas'
        ),

      // Selection Actions
      setSelectedTypeId: (typeId) =>
        set(
          (state) => ({
            selection: {
              ...state.selection,
              selectedTypeId: typeId
            }
          }),
          false,
          'editor/setSelectedTypeId'
        ),

      setSelectedElementId: (elementId) =>
        set(
          (state) => ({
            selection: {
              ...state.selection,
              selectedElementId: elementId,
              selectedElementIds: elementId ? [elementId] : []
            }
          }),
          false,
          'editor/setSelectedElementId'
        ),

      setSelectedElementIds: (elementIds) =>
        set(
          (state) => ({
            selection: {
              ...state.selection,
              selectedElementIds: elementIds,
              selectedElementId: elementIds.length === 1 ? elementIds[0] : null
            }
          }),
          false,
          'editor/setSelectedElementIds'
        ),

      addSelectedElementId: (elementId) =>
        set(
          (state) => {
            const newIds = state.selection.selectedElementIds.includes(elementId)
              ? state.selection.selectedElementIds
              : [...state.selection.selectedElementIds, elementId]
            return {
              selection: {
                ...state.selection,
                selectedElementIds: newIds,
                selectedElementId: newIds.length === 1 ? newIds[0] : null
              }
            }
          },
          false,
          'editor/addSelectedElementId'
        ),

      removeSelectedElementId: (elementId) =>
        set(
          (state) => {
            const newIds = state.selection.selectedElementIds.filter((id) => id !== elementId)
            return {
              selection: {
                ...state.selection,
                selectedElementIds: newIds,
                selectedElementId: newIds.length === 1 ? newIds[0] : null
              }
            }
          },
          false,
          'editor/removeSelectedElementId'
        ),

      setHoveredElementId: (elementId) =>
        set(
          (state) => ({
            selection: {
              ...state.selection,
              hoveredElementId: elementId
            }
          }),
          false,
          'editor/setHoveredElementId'
        ),

      clearSelection: () =>
        set(
          (state) => ({
            selection: {
              ...state.selection,
              selectedTypeId: null,
              selectedElementId: null,
              selectedElementIds: [],
              hoveredElementId: null
            }
          }),
          false,
          'editor/clearSelection'
        ),

      // Visualization Actions
      setElementCount: (count) =>
        set(
          (state) => ({
            visualization: {
              ...state.visualization,
              elementCount: count
            }
          }),
          false,
          'editor/setElementCount'
        ),

      setObjectsByLayer: (objects) =>
        set(
          (state) => ({
            visualization: {
              ...state.visualization,
              objectsByLayer: objects
            }
          }),
          false,
          'editor/setObjectsByLayer'
        ),

      setRelationshipCount: (count) =>
        set(
          (state) => ({
            visualization: {
              ...state.visualization,
              relationshipCount: count
            }
          }),
          false,
          'editor/setRelationshipCount'
        ),

      updateVisualization: (elementCount, objectsByLayer, relationshipCount) =>
        set(
          {
            visualization: {
              elementCount,
              objectsByLayer,
              relationshipCount
            }
          },
          false,
          'editor/updateVisualization'
        ),

      // Relationship Edit Actions
      startRelationshipEdit: (relationKey, propertyKey, targetElementIds) =>
        set(
          {
            relationshipEdit: {
              isEditing: true,
              relationKey,
              propertyKey,
              targetElementIds
            }
          },
          false,
          'editor/startRelationshipEdit'
        ),

      updateRelationshipTargets: (targetElementIds) =>
        set(
          (state) => ({
            relationshipEdit: {
              ...state.relationshipEdit,
              targetElementIds
            }
          }),
          false,
          'editor/updateRelationshipTargets'
        ),

      endRelationshipEdit: () =>
        set(
          {
            relationshipEdit: initialRelationshipEditState
          },
          false,
          'editor/endRelationshipEdit'
        ),

      // Object Size Actions
      setDefaultObjectSize: (size) =>
        set(
          { defaultObjectSize: size },
          false,
          'editor/setDefaultObjectSize'
        ),

      // Reset Actions
      resetEditor: () =>
        set(
          {
            canvas: initialCanvasState,
            selection: initialSelectionState,
            visualization: initialVisualizationState,
            relationshipEdit: initialRelationshipEditState,
            defaultObjectSize: 100
          },
          false,
          'editor/resetEditor'
        )
    }),
    { name: 'EditorStore' }
  )
)

/**
 * Selectors for Optimized Component Re-renders
 */

// Canvas Selectors
export const useCanvasState = () => useEditorStore((state) => state.canvas)
export const useZoom = () => useEditorStore((state) => state.canvas.zoom)
export const usePan = () => useEditorStore((state) => state.canvas.pan)
export const useRotation = () => useEditorStore((state) => state.canvas.rotation)
export const useCanvasSize = () =>
  useEditorStore((state) => ({
    width: state.canvas.width,
    height: state.canvas.height
  }))

// Selection Selectors
export const useSelectionState = () => useEditorStore((state) => state.selection)
export const useSelectedTypeId = () => useEditorStore((state) => state.selection.selectedTypeId)
export const useSelectedElementId = () =>
  useEditorStore((state) => state.selection.selectedElementId)
export const useSelectedElementIds = () =>
  useEditorStore((state) => state.selection.selectedElementIds)
export const useHoveredElementId = () =>
  useEditorStore((state) => state.selection.hoveredElementId)

// Visualization Selectors
export const useVisualizationState = () => useEditorStore((state) => state.visualization)
export const useElementCount = () => useEditorStore((state) => state.visualization.elementCount)
export const useObjectsByLayer = () =>
  useEditorStore((state) => state.visualization.objectsByLayer)
export const useRelationshipCount = () =>
  useEditorStore((state) => state.visualization.relationshipCount)

// Relationship Edit Selectors
export const useRelationshipEditState = () => useEditorStore((state) => state.relationshipEdit)
export const useIsEditingRelationship = () =>
  useEditorStore((state) => state.relationshipEdit.isEditing)
export const useRelationshipEditTargets = () =>
  useEditorStore((state) => state.relationshipEdit.targetElementIds)

// Action Selectors (useShallow prevents unnecessary re-renders)
export const useCanvasActions = () =>
  useEditorStore(useShallow((state) => ({
    setZoom: state.setZoom,
    setPan: state.setPan,
    setRotation: state.setRotation,
    setCanvasSize: state.setCanvasSize,
    resetCanvas: state.resetCanvas
  })))

export const useSelectionActions = () =>
  useEditorStore(useShallow((state) => ({
    setSelectedTypeId: state.setSelectedTypeId,
    setSelectedElementId: state.setSelectedElementId,
    setSelectedElementIds: state.setSelectedElementIds,
    addSelectedElementId: state.addSelectedElementId,
    removeSelectedElementId: state.removeSelectedElementId,
    setHoveredElementId: state.setHoveredElementId,
    clearSelection: state.clearSelection
  })))

export const useVisualizationActions = () =>
  useEditorStore(useShallow((state) => ({
    setElementCount: state.setElementCount,
    setObjectsByLayer: state.setObjectsByLayer,
    setRelationshipCount: state.setRelationshipCount,
    updateVisualization: state.updateVisualization
  })))

export const useRelationshipEditActions = () =>
  useEditorStore(useShallow((state) => ({
    startRelationshipEdit: state.startRelationshipEdit,
    updateRelationshipTargets: state.updateRelationshipTargets,
    endRelationshipEdit: state.endRelationshipEdit
  })))

// Object Size Selectors
export const useDefaultObjectSize = () => useEditorStore((state) => state.defaultObjectSize)
export const useSetDefaultObjectSize = () => useEditorStore((state) => state.setDefaultObjectSize)
